import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Mail, Phone, Linkedin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const PEOPLE = [
  {
    key: "owen",
    initial: "O",
    name: "Owen Michael Peterson",
    title: "Founder",
    copy: [
      "Owen leads ArcPoint's executive search work, client relationships and business development.",
      "He works directly with businesses on senior hiring requirements, market mapping and executive search assignments.",
    ],
    email: "owen@arcpointgroup.co.uk",
    phone: "+44 7503 410642",
    linkedin: "https://www.linkedin.com/in/owen-peterson-executive-search",
    linkedinLabel: "LinkedIn — Owen Peterson",
  },
  {
    key: "mei",
    initial: "M",
    name: "Mei Victoria Peterson",
    title: "Operations & Brand",
    copy: [
      "Mei works across ArcPoint's day-to-day operations, website, brand and business projects.",
      "Her role is developing as ArcPoint grows, so she works across whatever needs improving, building or organising behind the scenes.",
    ],
    email: "mei@arcpointgroup.co.uk",
    phone: "+44 7770 094120",
    linkedin: null,
    linkedinLabel: null,
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Owen delivered a relevant shortlist inside five weeks. Every candidate was worth interviewing, and we made an appointment from the first round.",
    role: "Founder & CEO",
    context: "B2B SaaS · Series B",
  },
  {
    quote:
      "Straightforward to work with. No inflated pipelines, no handovers, and honest about the trade-offs on each candidate.",
    role: "Chair",
    context: "Growth-stage software company",
  },
  {
    quote:
      "The mandate was handled personally from brief to offer. We appointed a COO who is still with the business two years on.",
    role: "Managing Director",
    context: "PE-backed SME",
  },
];

function PersonCard({ person, index }) {
  return (
    <motion.article
      data-testid={`person-card-${person.key}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative border border-white/[0.06] bg-[#0A1F40]/40 hover:border-[#C9A876]/25 transition-colors duration-500 p-7 md:p-8"
    >
      <div className="flex items-center gap-2 mb-5">
        <span className="block h-[1px] w-6 bg-[#C9A876]/80" />
        <span className="font-mono-eyebrow text-[#C9A876] text-[10px]">
          ArcPoint
        </span>
      </div>

      <h3
        className="font-display text-2xl md:text-3xl tracking-tight leading-tight text-white"
        data-testid={`person-name-${person.key}`}
      >
        {person.name}
      </h3>
      <div className="mt-2.5 font-mono-eyebrow text-[#C9A876] text-[11px]">
        {person.title}
      </div>

      <div className="hairline my-5" />

      <div
        className="space-y-3 text-sm leading-relaxed text-slate-200/85"
        data-testid={`person-copy-${person.key}`}
      >
        {person.copy.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-2">
        <a
          href={`mailto:${person.email}`}
          data-testid={`person-email-${person.key}`}
          className="inline-flex items-center gap-2.5 text-[13px] text-slate-300/80 hover:text-[#C9A876] transition-colors"
        >
          <Mail size={13} strokeWidth={1.5} className="text-[#C9A876]/80" />
          {person.email}
        </a>
        {person.phone && (
          <a
            href={`tel:${person.phone.replace(/\s+/g, "")}`}
            data-testid={`person-phone-${person.key}`}
            className="inline-flex items-center gap-2.5 text-[13px] text-slate-300/80 hover:text-[#C9A876] transition-colors"
          >
            <Phone size={13} strokeWidth={1.5} className="text-[#C9A876]/80" />
            {person.phone}
          </a>
        )}
        {person.linkedin && (
          <a
            href={person.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`person-linkedin-${person.key}`}
            className="inline-flex items-center gap-2.5 text-[13px] text-slate-300/80 hover:text-[#C9A876] transition-colors"
          >
            <Linkedin size={13} strokeWidth={1.5} className="text-[#C9A876]/80" />
            {person.linkedinLabel}
          </a>
        )}
      </div>
    </motion.article>
  );
}

export default function PeoplePage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "People — ArcPoint Executive Search";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) {
      meta.setAttribute(
        "content",
        "The people behind ArcPoint — a small, founder-led UK executive search business. Meet Owen and Mei."
      );
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main
      data-testid="page-people"
      className="bg-[#0E2750] text-white min-h-screen"
    >
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 md:pt-52 pb-16 md:pb-24 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-[#C9A876]/[0.05] blur-[180px]" />
        <div className="pointer-events-none absolute bottom-0 -left-20 h-[420px] w-[420px] rounded-full bg-[#1d3b6e]/40 blur-[160px]" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="people-back-link"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-300/70 hover:text-[#C9A876] transition-colors mb-12"
          >
            <ArrowLeft size={13} strokeWidth={1.5} /> Back to ArcPoint
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="block h-[1px] w-10 bg-[#C9A876]/80" />
            <span className="font-mono-eyebrow text-[#C9A876]">ArcPoint</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-4xl sm:text-5xl lg:text-[76px] tracking-tight leading-[1.02] text-white max-w-4xl"
            data-testid="people-heading"
          >
            The people behind ArcPoint.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-10 max-w-3xl space-y-5 text-base md:text-lg leading-relaxed text-slate-200/85"
          >
            <p>ArcPoint is a small, founder-led business.</p>
            <p>
              We work closely with clients and candidates, without unnecessary
              layers of account management or handovers.
            </p>
            <p className="text-sm text-slate-300/60 uppercase tracking-[0.22em] pt-2">
              Based in the United Kingdom
            </p>
          </motion.div>
        </div>
      </section>

      {/* People grid */}
      <section className="relative bg-[#0E2750] py-16 md:py-24 border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {PEOPLE.map((p, i) => (
              <PersonCard key={p.key} person={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials strip — anonymised, discreet */}
      <section
        data-testid="people-testimonials"
        className="relative bg-[#0A1F40] py-20 md:py-28 border-t border-white/[0.06]"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-center gap-3 mb-12 md:mb-16">
            <span className="block h-[1px] w-10 bg-[#C9A876]/80" />
            <span className="font-mono-eyebrow text-[#C9A876]">
              ◆ In their words
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={i}
                data-testid={`people-testimonial-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className="bg-[#0A1F40] px-8 py-10 md:px-9 md:py-12 flex flex-col justify-between"
              >
                <div>
                  <span
                    aria-hidden
                    className="font-display text-[#C9A876]/70 text-5xl leading-none block"
                  >
                    “
                  </span>
                  <blockquote className="mt-4 text-[15px] md:text-base leading-relaxed text-slate-100/90 font-display-italic">
                    {t.quote}
                  </blockquote>
                </div>
                <figcaption className="mt-8 pt-6 border-t border-white/[0.06]">
                  <div className="text-sm text-white/90">{t.role}</div>
                  <div className="font-mono-eyebrow text-slate-300/60 mt-1.5">
                    {t.context}
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>

          <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-slate-400/60">
            Client identities withheld — retained search is handled in confidence.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#0A1F40] py-24 md:py-32 border-t border-white/[0.06] overflow-hidden">
        <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[360px] w-[820px] rounded-full bg-[#C9A876]/[0.05] blur-[160px]" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <span className="font-mono-eyebrow text-[#C9A876]">◆ Get in touch</span>
          <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white">
            Prefer a{" "}
            <span className="font-display-italic text-[#C9A876]">direct</span>{" "}
            conversation?
          </h2>
          <p className="mt-6 text-slate-300/80 leading-relaxed">
            Reach out and you'll speak with Owen directly — no gatekeepers, no
            account managers.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/#contact"
              data-testid="people-cta-contact"
              className="group inline-flex items-center gap-2 px-7 py-4 bg-[#C9A876] hover:bg-[#DCC298] text-[#0E2750] text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors duration-500"
            >
              Get in touch
              <ArrowUpRight
                size={14}
                strokeWidth={1.8}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
            <Link
              to="/"
              data-testid="people-secondary-cta"
              className="text-[11px] uppercase tracking-[0.22em] text-slate-300/70 hover:text-[#C9A876] transition-colors"
            >
              Return to ArcPoint →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
